// The industry-page masthead constellation: the vertical's icon drawn as
// glowing cyan points on a plain 2D canvas — the same dot/halo language and
// staggered reveal as the trends hero, plus a slow perpetual twinkle so the
// band stays alive like the landing page's video. Pure decoration
// (aria-hidden, behind a scrim); everything is generated in code, no payload.

const GLOW = "124, 226, 255" // glacier cyan #7CE2FF
const REVEAL_MS = 1400

// Icon outlines as closed polygons in a 100x100 unit space (y down). Corner
// indexes get the brightest anchor stars; the in-between vertices only shape
// the outline walk (the neck curve reads as a curve, not as five stars). An
// icon made of several silhouettes lists them as `parts`; an `open` part is a
// wall the walk doesn't close back to its start (a floor plan's partitions, or
// an outline with a doorway as the gap between its ends) while still bounding
// the interior sprinkle as if it were closed; `marks` are anchor stars placed
// inside the outline (a die's pips) that the interior sprinkle keeps clear of,
// and `interior` caps that sprinkle where it would compete with the marks.
const SHAPES = {
  shirt: {
    outline: [
      [36, 15], // left neck top
      [41, 20], [46, 22.5], [50, 23], [54, 22.5], [59, 20], // neck dip
      [64, 15], // right neck top
      [82, 21], // right shoulder tip
      [97, 43], // right sleeve, outer end
      [78, 53], // right sleeve cuff
      [72.5, 42], // right underarm
      [72.5, 89], // right hem
      [27.5, 89], // left hem
      [27.5, 42], // left underarm
      [22, 53], // left sleeve cuff
      [3, 43], // left sleeve, outer end
      [18, 21] // left shoulder tip
    ],
    corners: [0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]
  },
  // A die face-on, showing five: the rounded square is the outline, the pips
  // are marks.
  die: {
    outline: [
      [22, 14], [78, 14], // top edge
      [83.7, 16.3], [86, 22], // top-right corner
      [86, 78], [83.7, 83.7], [78, 86], // bottom-right corner
      [22, 86], [16.3, 83.7], [14, 78], // bottom-left corner
      [14, 22], [16.3, 16.3] // top-left corner
    ],
    corners: [2, 5, 8, 11],
    marks: [[32, 32], [68, 32], [50, 50], [32, 68], [68, 68]],
    interior: 10
  },
  // An umbrella: domed canopy with a finial, four scallops along the hem, a
  // straight stem ending in a J hook. The stem is a 3-unit strip so both of
  // its edges get walked.
  umbrella: {
    outline: [
      [5, 52], // left tip
      [8.4, 35.9], [18.2, 22.3], [32.8, 13.2], [42.2, 10.6], // left canopy
      [50, 5], // finial
      [57.8, 10.6], [67.2, 13.2], [81.8, 22.3], [91.6, 35.9], // right canopy
      [95, 52], // right tip
      [84.1, 46], [73.3, 52], [62.4, 46], [51.5, 52], // right scallops, to the stem
      [51.5, 80], [49.4, 84.9], [44.5, 87], [39.6, 84.9], [37.5, 80], // stem + hook, outer
      [40.5, 80], [41.7, 82.8], [44.5, 84], [47.3, 82.8], [48.5, 80], // hook, inner
      [48.5, 52], // stem, left edge back up
      [37.6, 46], [27, 52], [16, 46] // left scallops, to the tip
    ],
    corners: [0, 5, 10, 12, 17, 27]
  },
  // Fork and knife side by side, upright. Three tines, not four: at the
  // outline walk's star pitch four tines blur into a speckled block.
  cutlery: {
    parts: [
      {
        outline: [
          [35.5, 92], [35.5, 54], // handle, left edge
          [28.5, 46], // flare
          [28.5, 10], [31.5, 10], [31.5, 40], // tine 1
          [36.5, 40], [36.5, 10], [39.5, 10], [39.5, 40], // tine 2
          [44.5, 40], [44.5, 10], [47.5, 10], // tine 3
          [47.5, 46], // flare
          [40.5, 54], [40.5, 92], // handle, right edge
          [38, 94] // rounded end
        ],
        corners: [2, 3, 7, 11, 13, 16]
      },
      {
        outline: [
          [65.5, 92], [65.5, 52], // handle, left edge
          [61, 46], [58.5, 36], [58.8, 24], [61.5, 14], // cutting edge
          [67, 7], // tip
          [70.5, 14], [71.5, 26], [71, 40], [70.5, 52], // spine
          [70.5, 92], // handle, right edge
          [68, 94] // rounded end
        ],
        corners: [2, 6, 12]
      }
    ]
  },
  // A shopping centre's floor plan: two anchor stores open onto the mall
  // street, three shop units line it above, and below a unit either side of
  // the entrance passage, whose doorway is the gap in the outer wall. Anchor
  // stars on the shopfront corners and junctions keep the partitions legible;
  // the sprinkle stays light, shoppers in the rooms rather than a filled
  // silhouette.
  plan: {
    parts: [
      {
        outline: [
          [56.7, 72], [70, 72], [70, 80], [96, 80], // entrance, right jamb; right anchor
          [96, 20], [70, 20], [70, 28], [30, 28], [30, 20], [4, 20], // top
          [4, 80], [30, 80], [30, 72], [43.3, 72] // left anchor; entrance, left jamb
        ],
        corners: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
        open: true
      },
      { outline: [[30, 28], [30, 43], [70, 43], [70, 28]], corners: [1, 2], open: true }, // upper shopfronts
      { outline: [[43.3, 28], [43.3, 43]], corners: [1], open: true }, // upper partitions
      { outline: [[56.7, 28], [56.7, 43]], corners: [1], open: true },
      { outline: [[30, 72], [30, 57], [43.3, 57], [43.3, 72]], corners: [1, 2], open: true }, // lower shopfronts
      { outline: [[56.7, 72], [56.7, 57], [70, 57], [70, 72]], corners: [1, 2], open: true }
    ],
    interior: 16
  }
}

export function initIndustryHeros() {
  document.querySelectorAll("[data-industry-hero]").forEach((el) => {
    const shape = SHAPES[el.dataset.industryHero]
    if (shape) mountHero(el, shape)
  })
}

// Deterministic PRNG (mulberry32) so the constellation is the same sky on
// every visit — stars may twinkle, they don't rearrange.
function rng(seed) {
  let a = seed
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function segmentDistance([px, py], [ax, ay], [bx, by]) {
  const dx = bx - ax, dy = by - ay
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

// The edges a part's outline walk draws: a closed outline wraps back to its
// first vertex, an open one stops at its last.
function segments({ outline, open }) {
  const n = open ? outline.length - 1 : outline.length
  return Array.from({ length: n }, (_, i) => [outline[i], outline[(i + 1) % outline.length]])
}

function inPolygon([px, py], poly) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j]
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

// Builds the star field in unit space: bright anchors on the icon's corners,
// jittered stars walked along the outline, and a sparse interior sprinkle
// kept clear of the outline so the silhouette stays crisp.
function buildStars(shape) {
  const rand = rng(47)
  const parts = shape.parts || [shape]
  const marks = shape.marks || []
  const stars = []
  const anchor = (x, y, core, alpha) => {
    stars.push({ x, y, core, alpha, phase: rand() * Math.PI * 2, period: 2600 + rand() * 2400 })
  }

  for (const part of parts) {
    const { outline, corners } = part
    for (const i of corners) anchor(outline[i][0], outline[i][1], 2.5, 0.8)

    const step = 4.4
    let carry = 0
    for (const [a, b] of segments(part)) {
      const len = Math.hypot(b[0] - a[0], b[1] - a[1])
      for (let d = carry; d < len; d += step) {
        const t = d / len
        stars.push({
          x: a[0] + (b[0] - a[0]) * t + (rand() - 0.5) * 1.2,
          y: a[1] + (b[1] - a[1]) * t + (rand() - 0.5) * 1.2,
          core: 1.3 + rand() * 0.8, alpha: 0.55 + rand() * 0.2,
          phase: rand() * Math.PI * 2, period: 2600 + rand() * 2400
        })
      }
      carry = ((carry - len) % step + step) % step
    }
  }

  for (const [x, y] of marks) anchor(x, y, 3.2, 0.9)

  const interior = []
  const want = shape.interior ?? 42
  for (let tries = 0; tries < 900 && interior.length < want; tries++) {
    const p = [3 + rand() * 94, 15 + rand() * 74]
    if (!parts.some(({ outline }) => inPolygon(p, outline))) continue
    let edge = Infinity
    for (const part of parts) {
      for (const [a, b] of segments(part)) edge = Math.min(edge, segmentDistance(p, a, b))
    }
    if (edge < 5.5) continue
    if (marks.some(([qx, qy]) => Math.hypot(p[0] - qx, p[1] - qy) < 10)) continue
    if (interior.some(([qx, qy]) => Math.hypot(p[0] - qx, p[1] - qy) < 7)) continue
    interior.push(p)
    stars.push({ x: p[0], y: p[1], core: 0.8 + rand() * 0.7, alpha: 0.3 + rand() * 0.15, phase: rand() * Math.PI * 2, period: 2600 + rand() * 2400 })
  }

  // Reveal in shuffled order so the icon condenses out of scattered stars
  // instead of being traced like a pen stroke.
  for (let i = stars.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [stars[i], stars[j]] = [stars[j], stars[i]]
  }
  return stars
}

function mountHero(el, shape) {
  const canvas = document.createElement("canvas")
  canvas.setAttribute("aria-hidden", "true")
  canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;"
  el.appendChild(canvas)

  const stars = buildStars(shape)
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const ctx = canvas.getContext("2d")
  let raf = 0
  let revealed = reduced
  let visible = true

  // Contain-fit into the right column on wide viewports (the headline owns
  // the left), centered when the band is narrow.
  const transform = () => {
    const w = el.clientWidth, h = el.clientHeight
    if (!w || !h) return null
    const pad = 28
    const fitW = w >= 1024 ? w * 0.36 : w
    const scale = Math.min((fitW - 2 * pad) / 100, (h - 2 * pad) / 100)
    const offX = w >= 1024 ? w - pad - w * 0.03 - 100 * scale : (w - 100 * scale) / 2
    return { w, h, scale, offX, offY: (h - 100 * scale) / 2 }
  }

  const dot = (s, x, y, scale, alpha) => {
    ctx.beginPath()
    ctx.fillStyle = `rgba(${GLOW}, ${alpha * 0.16})`
    ctx.arc(x, y, s.core * 3, 0, 2 * Math.PI)
    ctx.fill()
    ctx.beginPath()
    ctx.fillStyle = `rgba(${GLOW}, ${alpha})`
    ctx.arc(x, y, s.core, 0, 2 * Math.PI)
    ctx.fill()
  }

  const paint = (now) => {
    const t = transform()
    if (!t) return false
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    if (canvas.width !== Math.round(t.w * dpr) || canvas.height !== Math.round(t.h * dpr)) {
      canvas.width = Math.round(t.w * dpr)
      canvas.height = Math.round(t.h * dpr)
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, t.w, t.h)
    for (const s of stars) {
      const twinkle = reduced ? 1 : 0.72 + 0.28 * Math.sin((now / s.period) * 2 * Math.PI + s.phase)
      dot(s, t.offX + s.x * t.scale, t.offY + s.y * t.scale, t.scale, s.alpha * twinkle)
    }
    return true
  }

  const loop = (now) => {
    raf = 0
    if (!visible || reduced) return
    if (paint(now)) raf = requestAnimationFrame(loop)
  }

  // Staggered reveal (progressive draw, no clearing), then hand off to the
  // twinkle loop. Skipped straight to a static paint under reduced motion.
  const reveal = () => {
    const t = transform()
    if (!t) { revealed = true; return }
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.round(t.w * dpr)
    canvas.height = Math.round(t.h * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    const perFrame = Math.max(2, Math.ceil(stars.length / (REVEAL_MS / 16)))
    let i = 0
    const step = (now) => {
      for (const end = Math.min(i + perFrame, stars.length); i < end; i++) {
        const s = stars[i]
        dot(s, t.offX + s.x * t.scale, t.offY + s.y * t.scale, t.scale, s.alpha)
      }
      if (i < stars.length) {
        raf = requestAnimationFrame(step)
      } else {
        revealed = true
        raf = requestAnimationFrame(loop)
      }
    }
    raf = requestAnimationFrame(step)
  }

  // The twinkle only runs while the band is actually on screen.
  new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting
    if (visible && revealed && !raf) raf = requestAnimationFrame(loop)
  }).observe(el)

  new ResizeObserver(() => {
    if (!revealed) return
    if (reduced || !visible) paint(performance.now())
  }).observe(el)

  if (reduced) {
    paint(performance.now())
  } else {
    reveal()
  }
}
